import React from 'react'
import {
  Card,
  CardBody,
  CardHeader,
  Heading,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Progress,
  VStack,
  HStack,
  Text,
  Badge,
  Divider,
  Box,
  CircularProgress,
  CircularProgressLabel,
} from '@chakra-ui/react'

interface UserStats {
  total_submissions: number
  successful_submissions: number
  problems_solved: number
  total_points: number
  current_rank?: number
  average_runtime?: number
  best_runtime?: number
  fastest_problem?: string
  days_active: number
  longest_streak: number
  current_streak: number
  last_submission?: string
  languages_used?: Record<string, number>
  achievements?: string[]
  recent_problems?: Array<{
    const_id: string
    title: string
    status: string
    time_created: string
  }>
}

interface UserStatsCardProps {
  stats: UserStats
}

const UserStatsCard: React.FC<UserStatsCardProps> = ({ stats }) => {
  const successRate = stats.total_submissions > 0 
    ? (stats.successful_submissions / stats.total_submissions) * 100 
    : 0

  const formatRuntime = (runtime: number) => {
    if (runtime < 0.001) return '<1ms'
    if (runtime < 1) return `${(runtime * 1000).toFixed(1)}ms`
    return `${runtime.toFixed(3)}s`
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  return (
    <Card>
      <CardHeader>
        <Heading size="md">Performance Statistics</Heading>
      </CardHeader>
      <CardBody>
        <VStack spacing={6} align="stretch">
          {/* Primary Stats */}
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
            <Stat>
              <StatLabel>Rank</StatLabel>
              <StatNumber color="blue.500">
                #{stats.current_rank || 'N/A'}
              </StatNumber>
              <StatHelpText>Global ranking</StatHelpText>
            </Stat>

            <Stat>
              <StatLabel>Total Points</StatLabel>
              <StatNumber color="green.500">
                {stats.total_points.toLocaleString()}
              </StatNumber>
              <StatHelpText>Accumulated points</StatHelpText>
            </Stat>

            <Stat>
              <StatLabel>Problems Solved</StatLabel>
              <StatNumber color="purple.500">
                {stats.problems_solved}
              </StatNumber>
              <StatHelpText>Unique completions</StatHelpText>
            </Stat>

            <Stat>
              <StatLabel>Success Rate</StatLabel>
              <StatNumber color={successRate >= 75 ? 'green.500' : successRate >= 50 ? 'yellow.500' : 'red.500'}>
                {successRate.toFixed(1)}%
              </StatNumber>
              <StatHelpText>
                {stats.successful_submissions}/{stats.total_submissions} submissions
              </StatHelpText>
            </Stat>
          </SimpleGrid>

          <Divider />

          {/* Performance Metrics */}
          <VStack spacing={4} align="stretch">
            <Heading size="sm" color="gray.600">Performance Metrics</Heading>
            
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              {/* Success Rate Progress */}
              <Box>
                <HStack justify="space-between" mb={2}>
                  <Text fontSize="sm" fontWeight="medium">Success Rate</Text>
                  <Text fontSize="sm" color="gray.600">{successRate.toFixed(1)}%</Text>
                </HStack>
                <Progress 
                  value={successRate} 
                  colorScheme={successRate >= 75 ? 'green' : successRate >= 50 ? 'yellow' : 'red'}
                  size="lg"
                  hasStripe
                />
              </Box>

              {/* Runtime Stats */}
              {stats.average_runtime && (
                <VStack align="start" spacing={2}>
                  <Text fontSize="sm" fontWeight="medium">Runtime Performance</Text>
                  <HStack spacing={4}>
                    <VStack align="center" spacing={1}>
                      <Text fontSize="xs" color="gray.500">Average</Text>
                      <Badge colorScheme="blue" variant="subtle">
                        {formatRuntime(stats.average_runtime)}
                      </Badge>
                    </VStack>
                    {stats.best_runtime && (
                      <VStack align="center" spacing={1}>
                        <Text fontSize="xs" color="gray.500">Best</Text>
                        <Badge colorScheme="green" variant="subtle">
                          {formatRuntime(stats.best_runtime)}
                        </Badge>
                      </VStack>
                    )}
                  </HStack>
                  {stats.fastest_problem && (
                    <Text fontSize="xs" color="gray.600">
                      Fastest on: {stats.fastest_problem}
                    </Text>
                  )}
                </VStack>
              )}
            </SimpleGrid>
          </VStack>

          <Divider />

          {/* Activity Stats */}
          <VStack spacing={4} align="stretch">
            <Heading size="sm" color="gray.600">Activity</Heading>
            
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
              <VStack align="center" spacing={2}>
                <CircularProgress 
                  value={Math.min((stats.current_streak / 30) * 100, 100)} 
                  color="orange.400"
                  size="60px"
                >
                  <CircularProgressLabel fontSize="xs">
                    {stats.current_streak}
                  </CircularProgressLabel>
                </CircularProgress>
                <VStack spacing={0} align="center">
                  <Text fontSize="sm" fontWeight="medium">Current Streak</Text>
                  <Text fontSize="xs" color="gray.500">days</Text>
                </VStack>
              </VStack>

              <VStack align="center" spacing={2}>
                <CircularProgress 
                  value={Math.min((stats.longest_streak / 60) * 100, 100)} 
                  color="green.400"
                  size="60px"
                >
                  <CircularProgressLabel fontSize="xs">
                    {stats.longest_streak}
                  </CircularProgressLabel>
                </CircularProgress>
                <VStack spacing={0} align="center">
                  <Text fontSize="sm" fontWeight="medium">Longest Streak</Text>
                  <Text fontSize="xs" color="gray.500">days</Text>
                </VStack>
              </VStack>

              <VStack align="center" spacing={2}>
                <CircularProgress 
                  value={Math.min((stats.days_active / 365) * 100, 100)} 
                  color="blue.400"
                  size="60px"
                >
                  <CircularProgressLabel fontSize="xs">
                    {stats.days_active}
                  </CircularProgressLabel>
                </CircularProgress>
                <VStack spacing={0} align="center">
                  <Text fontSize="sm" fontWeight="medium">Days Active</Text>
                  <Text fontSize="xs" color="gray.500">total</Text>
                </VStack>
              </VStack>

              {stats.last_submission && (
                <VStack align="center" spacing={2}>
                  <Box 
                    w="60px" 
                    h="60px" 
                    borderRadius="full" 
                    bg="purple.100" 
                    display="flex" 
                    alignItems="center" 
                    justifyContent="center"
                  >
                    <Text fontSize="lg">📅</Text>
                  </Box>
                  <VStack spacing={0} align="center">
                    <Text fontSize="sm" fontWeight="medium">Last Activity</Text>
                    <Text fontSize="xs" color="gray.500">
                      {formatDate(stats.last_submission)}
                    </Text>
                  </VStack>
                </VStack>
              )}
            </SimpleGrid>
          </VStack>

          {/* Language Breakdown */}
          {stats.languages_used && Object.keys(stats.languages_used).length > 0 && (
            <>
              <Divider />
              <VStack spacing={4} align="stretch">
                <Heading size="sm" color="gray.600">Languages Used</Heading>
                <HStack spacing={2} flexWrap="wrap">
                  {Object.entries(stats.languages_used).map(([language, count]) => (
                    <Badge key={language} colorScheme="blue" variant="outline">
                      {language.toUpperCase()}: {count}
                    </Badge>
                  ))}
                </HStack>
              </VStack>
            </>
          )}
        </VStack>
      </CardBody>
    </Card>
  )
}

export default UserStatsCard 